<script lang="ts" setup>
import { computed, onBeforeUnmount, watch } from 'vue';

import { useSlotText } from '@/kasten-components/composables';
import type { VueClass } from '@/kasten-components/types';
import { cnMerge } from '@/utils/utils.js';
import ArrowDown from '~icons/lucide/arrow-down';
import ArrowUp from '~icons/lucide/arrow-up';

import { injectTableContext, isInsideScrollWrapperContext } from './model';

const { sortState, isTableEmpty, isSmall, isLoading, onIncludeColumn, onSort } =
  injectTableContext();
const isInsideScrollWrapper = isInsideScrollWrapperContext();

const props = withDefaults(
  defineProps<{
    name: string;
    label?: string;
    class?: VueClass;
    isActionColumn?: boolean;
    isCornerCell?: boolean;
  }>(),
  {
    class: '',
    isActionColumn: false,
    isCornerCell: false,
  },
);

const isSortable = computed(
  () => !isTableEmpty.value && sortState.value.sortableColumns.has(props.name),
);

const isSortedByThisColumn = computed(() => sortState.value.currentColumn === props.name);

const isDesc = computed(() => {
  // By default, if column is not picked for current sort it should look like desc
  if (!isSortedByThisColumn.value) {
    return true;
  }
  return sortState.value.isDesc;
});

const ariaSort = computed(() => {
  if (!isSortable.value) {
    return undefined;
  }

  if (!isSortedByThisColumn.value) {
    return 'none';
  }
  return sortState.value.isDesc ? 'descending' : 'ascending';
});

const slotText = useSlotText({ name: 'default' });

const sortButtonAriaLabel = computed(() => {
  const columnLabel = props.label?.trim() || slotText.value || props.name;

  if (!isSortedByThisColumn.value) {
    return `Sort by ${columnLabel} ascending.`;
  }

  const currentDir = sortState.value.isDesc ? 'descending' : 'ascending';
  const nextDir = sortState.value.isDesc ? 'ascending' : 'descending';
  return `Sorted by ${columnLabel} ${currentDir}. Activate to sort ${nextDir}.`;
});

const handleSort = () => {
  if (!isSortable.value || isLoading.value) return;
  onSort(props.name);
};

const SortIcon = computed(() => (isDesc.value ? ArrowDown : ArrowUp));

watch(
  () => props.name,
  (newValue, prevValue) => {
    prevValue !== undefined && onIncludeColumn(prevValue, false);
    onIncludeColumn(newValue, true);
  },
  { immediate: true },
);

onBeforeUnmount(() => onIncludeColumn(props.name, false));
</script>

<template>
  <component
    :is="props.isCornerCell ? 'td' : 'th'"
    :scope="props.isCornerCell ? undefined : 'col'"
    :aria-sort="props.isCornerCell ? undefined : ariaSort"
    :class="
      cnMerge(
        'sticky top-0 z-10',
        'text-left text-[0.625rem] font-semibold uppercase',
        'border-gray-200 bg-white dark:bg-navy-950',
        // Styles for `before` pseudo, element that would work well with sticky header of the native table.
        // Basically provides curved borders with additional background layer for sticky scroll.
        // Is not needed when inside the scroll wrapper
        'before:absolute before:bottom-0 before:left-0 before:right-0 before:top-0 before:border-b before:content-[\'\'] before:dark:border-navy-600 dark:before:bg-navy-900/40',
        !isInsideScrollWrapper && [
          'before:border-t',
          'first:before:left-0 first:before:rounded-tl-lg first:before:border-l',
          'last:before:right-0 last:before:rounded-tr-lg last:before:border-r',
        ],
        {
          'p-3 px-2.5 md:p-4 md:px-3.5': !isSmall,
          'p-3 px-2.5 md:px-3.5 md:py-3.5': isSmall,
        },
        props.class,
      )
    "
  >
    <!-- Sortable column with button -->
    <button
      v-if="isSortable && !isLoading"
      type="button"
      :aria-label="sortButtonAriaLabel"
      :disabled="isLoading"
      class="relative z-10 flex w-full cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-navy-900"
      @click="handleSort"
    >
      <span
        :class="
          isSortedByThisColumn
            ? // uppercase added here to ensure consistent casing with non-sortable columns
              'font-bold uppercase text-gray-900 dark:font-semibold dark:text-gray-300'
            : 'uppercase text-gray-600 dark:text-gray-400'
        "
      >
        <slot></slot>
      </span>
      <SortIcon
        :aria-hidden="true"
        :class="
          cnMerge([
            'size-3.5 flex-shrink-0 text-gray-500 dark:text-gray-600',
            { 'text-gray-900 dark:text-gray-300': isSortedByThisColumn },
          ])
        "
      />
    </button>

    <!-- Non-sortable column -->
    <div v-else class="relative z-10 text-gray-600 dark:text-gray-400">
      <slot>
        <span v-if="props.isActionColumn" class="sr-only">Action Menu</span>
      </slot>
    </div>
  </component>
</template>
